import clsx from "clsx";
import { FileText, FileUp, Loader2, X } from "lucide-react";
import { useRef, useState } from "react";

import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Dialog, DialogBody } from "../../components/Dialog";
import { motion, Presence } from "../../components/presence";
import { Button, Field, IconButton, inputClass, Segmented, textareaClass } from "../../components/ui";
import { locale, t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useUnsavedDraft } from "../../state/unsavedDrafts";
import { Waiting } from "../../components/Waiting";

/** Formats lus par le serveur (PDF texte, Word, LibreOffice, RTF, texte). */
export const ACCEPTED = ".pdf,.docx,.docm,.doc,.odt,.rtf,.txt,.md,application/pdf,application/msword,text/plain";
export const MAX_BYTES = 15 * 1024 * 1024;

export function fileSize(bytes: number): string {
  if (bytes < 1024) return t("{n} o", { n: bytes });
  if (bytes < 1024 * 1024) return t("{n} Ko", { n: Math.round(bytes / 1024) });
  return t("{n} Mo", { n: (bytes / 1024 / 1024).toLocaleString(locale(), { maximumFractionDigits: 1 }) });
}

/** Import d'un ordre reçu : fichier déposé ou choisi, ou texte collé ; nom facultatif. L'erreur de lecture du
 *  serveur (photo, PDF numérisé, .doc) s'affiche ici, sans fermer la fenêtre. */
export function ImportDialog({
  initialFile,
  busy,
  error,
  onImport,
  onClose,
}: {
  initialFile?: File | null;
  busy: boolean;
  error: string | null;
  onImport: (input: { file: File | null; text: string; name: string }) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"file" | "text">("file");
  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const target = useRef<HTMLElement | null>(null);
  const notify = useUi((s) => s.notify);
  const tooBig = Boolean(file && file.size > MAX_BYTES);
  const ready = mode === "file" ? Boolean(file) && !tooBig : text.trim().length >= 40;

  // Texte collé, nom ou fichier choisi : un clic à côté ou Échap demandent avant de fermer, la fermeture de l'onglet
  // aussi (audit de persistance M4).
  const dirty = Boolean(text.trim() || name.trim() || file);
  useUnsavedDraft("received:import", dirty, { label: t("Ordre reçu à importer"), discard: onClose });

  const pick = (files: FileList | null) => {
    const chosen = files?.[0];
    if (chosen) setFile(chosen);
  };

  return (
    <Dialog
      title={t("Importer un ordre reçu")}
      subtitle={t("L'ordre est lu, découpé en paragraphes et analysé ; rien n'est reporté sans votre accord.")}
      width={580}
      onClose={onClose}
      dirty={dirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button
            disabled={busy}
            blocked={!ready}
            onBlocked={() => {
              // Préalable manquant : le fichier (ou le texte collé) est l'action attendue (R12).
              designate(target.current, mode === "text" ? "field" : "ring");
              notify({
                text:
                  mode === "text"
                    ? t("Collez le texte de l'ordre (40 caractères au moins).")
                    : tooBig
                      ? t("Fichier trop volumineux : 15 Mo au plus.")
                      : t("Choisissez d'abord le fichier de l'ordre."),
                tone: "info",
              });
            }}
            onClick={() => onImport({ file: mode === "file" ? file : null, text, name: name.trim() })}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
            {busy ? <Waiting>{t("Lecture de l'ordre…")}</Waiting> : t("Importer et analyser")}
          </Button>
        </>
      }
    >
      <DialogBody className="space-y-4">
        <Segmented
          label={t("Source de l'ordre")}
          value={mode}
          onChange={setMode}
          options={[
            { value: "file", label: t("Fichier") },
            { value: "text", label: t("Texte collé") },
          ]}
        />
        {mode === "file" ? (
          file ? (
            <div
              ref={(el) => {
                target.current = el;
              }}
              className={clsx("flex items-center gap-3 rounded-xl border border-line bg-canvas/50 px-3.5 py-3", motion("fade"))}
            >
              <FileText size={20} className="shrink-0 text-ink-soft" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-ui font-medium text-ink">{file.name}</p>
                <p className={clsx("text-meta", tooBig ? "text-danger" : "text-ink-muted")}>
                  {fileSize(file.size)}
                  {tooBig && ` — ${t("trop volumineux : 15 Mo au plus")}`}
                </p>
              </div>
              <IconButton size="sm" onClick={() => setFile(null)} title={t("Choisir un autre fichier")} aria-label={t("Choisir un autre fichier")}>
                <X size={16} />
              </IconButton>
            </div>
          ) : (
            <button
              ref={(el) => {
                target.current = el;
              }}
              type="button"
              onClick={() => input.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                pick(e.dataTransfer.files);
              }}
              className={clsx(
                "flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
                over ? "border-accent bg-accent-soft" : "border-line-strong hover:border-accent hover:bg-hover",
              )}
            >
              <FileUp size={24} className="text-ink-soft" />
              <span className="text-ui font-medium text-ink">{t("Déposez le fichier de l'ordre, ou cliquez pour le choisir")}</span>
              <span className="text-meta leading-snug text-ink-muted">
                {t("PDF, Word (.docx ou .doc), LibreOffice (.odt), RTF ou texte — 15 Mo au plus.")}
                <br />
                {t("Photos et PDF numérisés : la lecture des images n'est pas disponible, collez plutôt le texte.")}
              </span>
            </button>
          )
        ) : (
          <textarea
            ref={(el) => {
              target.current = el;
            }}
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            placeholder={t("Collez ici le texte de l'ordre (courriel, document ouvert ailleurs)…")}
            className={clsx(textareaClass, "min-h-[220px]")}
          />
        )}
        <Field label={t("Nom (facultatif)")}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder={mode === "file" && file ? file.name.replace(/\.[^.]+$/, "").replace(/_+/g, " ") : t("Ordre reçu du …")}
            className={inputClass}
          />
        </Field>
        <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
      </DialogBody>
      <input ref={input} type="file" accept={ACCEPTED} className="hidden" onChange={(e) => pick(e.target.files)} />
    </Dialog>
  );
}
